import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ShieldCheck, AlertCircle, Mail, ArrowLeft } from 'lucide-react';
import { subscriptionsApi } from '@/lib/subscriptions';

export default function EmailPreferences() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const token = params.get('t') || params.get('token');

  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState(null);
  const [dirty, setDirty] = useState({}); // { list_id: 'subscribe' | 'unsubscribe' }
  const [saving, setSaving] = useState(false);
  const [confirmUnsubAll, setConfirmUnsubAll] = useState(false);
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupSubmitting, setLookupSubmitting] = useState(false);
  const [lookupSent, setLookupSent] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    (async () => {
      const { ok, data, status } = await subscriptionsApi.getPreferences(token);
      if (ok && data && data.email) {
        setPrefs(data);
      } else if (status === 404) {
        setPrefs(null);
      }
      setLoading(false);
    })();
  }, [token]);

  function toggleList(list_id, currentlyActive) {
    setDirty((d) => ({
      ...d,
      [list_id]: currentlyActive ? 'unsubscribe' : 'subscribe',
    }));
  }

  function isOn(item) {
    const change = dirty[item.list_id];
    if (change === 'subscribe') return true;
    if (change === 'unsubscribe') return false;
    return item.status === 'active';
  }

  async function savePreferences() {
    const changes = Object.entries(dirty).map(([list_id, action]) => ({ list_id, action }));
    if (changes.length === 0) {
      toast.message('No changes to save.');
      return;
    }
    setSaving(true);
    try {
      const { ok, data } = await subscriptionsApi.updatePreferences(token, changes);
      if (ok && data.ok) {
        toast.success('Preferences saved.');
        // Re-fetch to reflect server truth
        const fresh = await subscriptionsApi.getPreferences(token);
        if (fresh.ok && fresh.data && fresh.data.email) setPrefs(fresh.data);
        setDirty({});
      } else {
        toast.error('Could not save preferences. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function unsubscribeAll() {
    setSaving(true);
    try {
      const { ok, data } = await subscriptionsApi.unsubscribeAll(token);
      if (ok && data.ok) {
        toast.success('You have been unsubscribed from all KGOB emails.');
        const fresh = await subscriptionsApi.getPreferences(token);
        if (fresh.ok && fresh.data && fresh.data.email) setPrefs(fresh.data);
        setDirty({});
        setConfirmUnsubAll(false);
      } else {
        toast.error('Could not unsubscribe. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function requestPreferenceLink(e) {
    e?.preventDefault();
    if (!lookupEmail.includes('@')) {
      toast.error('Please enter a valid email.');
      return;
    }
    setLookupSubmitting(true);
    try {
      // We re-use the subscription request endpoint: it will not create new subscriptions
      // if the email is suppressed or already subscribed — it just (re)issues a code path.
      // For lost-token recovery, we send them a verification code which when verified
      // returns the master_token.
      await subscriptionsApi.request({
        email: lookupEmail.trim(),
        list_id: 'journal',
        source: 'preferences_recovery',
        first_name: null,
        website: '',
      });
      setLookupSent(true);
    } finally {
      setLookupSubmitting(false);
    }
  }

  // ---------- RENDER ----------

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="email-preferences-page">
      <Helmet>
        <title>Email Preferences · KGOB CPAs &amp; Advisors</title>
        <meta name="description" content="Manage your KGOB email preferences and subscriptions." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-sm text-[#4C5A70] hover:text-[#0B2240] mb-8"
          data-testid="prefs-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to kgob.com
        </button>

        <div className="flex items-center gap-3 mb-3">
          <span className="h-px w-8 bg-[#D4AF37]" />
          <span className="font-['Inter'] text-[11px] font-semibold tracking-[0.22em] uppercase text-[#D4AF37]">
            Email Preferences
          </span>
        </div>

        <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#0B2240] mb-4 leading-tight">
          Manage your KGOB email.
        </h1>

        {loading && (
          <div className="text-sm text-[#4C5A70] mt-6">Loading your preferences…</div>
        )}

        {/* No token OR token not found → lookup form */}
        {!loading && !prefs && (
          <div
            className="mt-8 bg-white border border-[#E1DFD7] rounded-md p-6 sm:p-8"
            data-testid="prefs-lookup-card"
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-[11px] tracking-[0.18em] uppercase text-[#D4AF37] font-semibold">
                We couldn&rsquo;t find that link
              </span>
            </div>
            <h2 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">
              Get a fresh preferences link.
            </h2>
            <p className="text-sm text-[#4C5A70] mb-5 leading-relaxed">
              Enter your email and we&rsquo;ll send you a six-digit code. Confirm it and you&rsquo;ll land back here with full control of your subscriptions.
            </p>

            {lookupSent ? (
              <div
                className="bg-[#F4F1E7] border border-[#D4AF37]/40 rounded px-4 py-4 text-sm text-[#4C5A70]"
                data-testid="prefs-lookup-sent"
              >
                <strong className="text-[#0B2240] block mb-1">Check your inbox.</strong>
                If we have you on file, a verification code is on its way. Click the link inside the email to land back here.
              </div>
            ) : (
              <form data-testid="email-preferences-form-1" onSubmit={requestPreferenceLink} className="space-y-3">
                <Input
                  type="email"
                  placeholder="you@firm.com"
                  value={lookupEmail}
                  onChange={(e) => setLookupEmail(e.target.value)}
                  required
                  data-testid="prefs-lookup-email"
                  className="bg-[#F7F6F2] border-[#E1DFD7]"
                />
                <Button
                  type="submit"
                  disabled={lookupSubmitting}
                  className="w-full h-11 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold"
                  data-testid="prefs-lookup-submit"
                >
                  {lookupSubmitting ? 'Sending…' : 'Send me a recovery link'}
                </Button>
              </form>
            )}
            <p className="text-xs text-[#4C5A70]/70 mt-6">
              Still stuck? Email us at{' '}
              <a data-testid="email-preferences-a-2" href="mailto:connect@kgob.com" className="underline hover:text-[#0B2240]">
                connect@kgob.com
              </a>{' '}
              and we&rsquo;ll sort it manually.
            </p>
          </div>
        )}

        {/* Preferences loaded */}
        {!loading && prefs && (
          <>
            <p className="text-sm text-[#4C5A70] mb-8 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#D4AF37]" />
              You&rsquo;re signed in as <strong className="text-[#0B2240]">{prefs.email}</strong>
            </p>

            <div className="space-y-3 mb-10" data-testid="prefs-lists">
              {(prefs.lists || []).map((item) => (
                <div
                  key={item.list_id}
                  className="bg-white border border-[#E1DFD7] rounded-md p-5 flex items-start justify-between gap-4"
                  data-testid={`prefs-list-${item.list_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Playfair_Display'] text-lg text-[#0B2240] leading-tight">
                      {item.name}
                    </h3>
                    <p className="text-sm text-[#4C5A70] mt-1 leading-relaxed">
                      {item.short_description}
                    </p>
                    {item.subscribed_at && item.status === 'active' && (
                      <p className="text-[11px] text-[#4C5A70]/60 mt-2">
                        Subscribed since {new Date(item.subscribed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </p>
                    )}
                  </div>
                  <Switch
                    checked={isOn(item)}
                    onCheckedChange={() => toggleList(item.list_id, isOn(item))}
                    className="mt-1 data-[state=checked]:bg-[#D4AF37]"
                    data-testid={`prefs-toggle-${item.list_id}`}
                  />
                </div>
              ))}
            </div>

            <Button
              type="button"
              onClick={savePreferences}
              disabled={saving || Object.keys(dirty).length === 0}
              className="w-full sm:w-auto h-12 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold mb-10 disabled:opacity-60"
              data-testid="prefs-save"
            >
              {saving ? 'Saving…' : Object.keys(dirty).length === 0 ? 'No changes' : 'Save preferences'}
            </Button>

            <div className="border-t border-[#E1DFD7] pt-8">
              <p className="text-xs tracking-[0.18em] uppercase text-[#4C5A70] mb-3">Or step away entirely</p>
              {confirmUnsubAll ? (
                <div className="bg-white border border-[#E8C7B0] rounded-md p-5 space-y-4">
                  <p className="text-sm text-[#0B2240]">
                    This unsubscribes you from <strong>every</strong> KGOB list. We&rsquo;ll keep your record on file for legal purposes only — no future marketing.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      onClick={unsubscribeAll}
                      disabled={saving}
                      variant="destructive"
                      data-testid="prefs-confirm-unsub-all"
                    >
                      {saving ? 'Unsubscribing…' : 'Yes, unsubscribe me from everything'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setConfirmUnsubAll(false)}
                      data-testid="prefs-cancel-unsub-all"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmUnsubAll(true)}
                  className="text-sm text-[#B45F3E] hover:text-[#8A3F25] underline-offset-2 hover:underline"
                  data-testid="prefs-unsub-all"
                >
                  Unsubscribe from all KGOB emails
                </button>
              )}
            </div>

            <p className="text-xs text-[#4C5A70]/70 mt-10 flex items-start gap-2">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-[#D4AF37] flex-shrink-0" />
              <span>
                Your preferences are saved to your KGOB record and synced to our CRM. We never sell or share email addresses. Questions?{' '}
                <a data-testid="email-preferences-a-1" href="mailto:connect@kgob.com" className="underline hover:text-[#0B2240]">
                  connect@kgob.com
                </a>
              </span>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
